import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ethers } from 'ethers';
import { useParams, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, CircularProgress, Paper, Divider, Link, Button, TextField, Tooltip, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import { SERVER_NAME, genericTokenIcon, E3D_NFT_MANAGER_ADDRESS } from './config';
import NFTManagerABI from './E3DNFTManager_ABI.json';
import { useAuth } from './auth/AuthContext';

const E3D_ERC20_MIN_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function transfer(address to, uint256 value) returns (bool)',
];

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 1480,
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: { padding: '4px 6px 0px' },
  },
  hero: {
    padding: '16px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.28), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.12)',
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: { padding: '8px 10px', marginBottom: 8 },
  },
  heroTitleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  heroTitle: {
    fontSize: '1.6rem',
    fontWeight: 900,
    letterSpacing: -0.3,
    lineHeight: 1.15,
    color: '#e5e7ff',
    textShadow: '0 0 14px rgba(77,121,255,0.45)',
    wordBreak: 'break-word',
  },
  heroAddr: {
    fontSize: 12,
    fontFamily: 'monospace',
    color: '#9ea4d8',
    marginTop: 4,
    wordBreak: 'break-all',
  },
  heroNav: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
    flexShrink: 0,
  },
  navButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '6px 14px',
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#3e61cc' },
  },
  awakenButton: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    padding: '6px 14px',
    whiteSpace: 'nowrap',
  },
  fundingCtaButton: {
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    minWidth: 220,
    color: '#ffffff',
    backgroundColor: '#4d79ff',
    '&:hover': {
      backgroundColor: '#3e61cc',
    },
    '&.Mui-disabled': {
      color: 'rgba(255,255,255,0.72)',
      backgroundColor: 'rgba(77, 121, 255, 0.26)',
    },
  },
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
  },
  panelTitle: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  panelDivider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    cursor: 'pointer',
    userSelect: 'none',
  },
  sectionHeaderMain: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    flex: 1,
  },
  sectionArrow: {
    color: '#9ea4d8',
    fontSize: 16,
    lineHeight: 1,
    transformOrigin: 'center',
    transition: 'transform 0.18s ease',
    flexShrink: 0,
  },
  label: {
    color: '#9ea4d8',
    fontSize: 12,
  },
  value: {
    color: '#e5e7ff',
    fontSize: 13,
    wordBreak: 'break-word',
  },
  activityRow: {
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    padding: 10,
    background: 'rgba(5, 8, 20, 0.62)',
  },
  input: {
    '& .MuiInputBase-root': {
      color: '#e5e7ff',
      background: 'rgba(5, 8, 20, 0.62)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.10)',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiInputLabel-root': { color: '#9ea4d8' },
  },
}));

function toEtherscanTokenUrl(addr) {
  const a = String(addr || '').trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(a)) return '';
  return `https://etherscan.io/token/${a}`;
}

function toEtherscanAddressUrl(addr) {
  const a = String(addr || '').trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(a)) return '';
  return `https://etherscan.io/address/${a}`;
}

function toEtherscanTxUrl(tx) {
  const h = String(tx || '').trim();
  if (!/^0x[a-fA-F0-9]{64}$/.test(h)) return '';
  return `https://etherscan.io/tx/${h}`;
}

function toEtherscanNftTokenUrl(contractAddr, tokenId) {
  const a = String(contractAddr || '').trim();
  const id = String(tokenId || '').trim();
  if (!/^0x[a-fA-F0-9]{40}$/.test(a)) return '';
  if (!/^\d+$/.test(id)) return '';
  return `https://etherscan.io/token/${a}?a=${id}`;
}

function ipfsToHttpUrl(ipfsUri) {
  const s = String(ipfsUri || '').trim();
  const m = s.match(/^ipfs:\/\/(.+)$/i);
  if (!m) return '';
  const path = m[1].replace(/^ipfs\//i, '');
  return `https://ipfs.io/ipfs/${path}`;
}

function formatHeartbeat(iso) {
  const s = String(iso || '').trim();
  if (!s) return '';
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return s;
  if (d.getUTCFullYear() < 2000) return '';
  return s;
}

function normalizeDisplayIso(iso) {
  const s = String(iso || '').trim();
  if (!s) return '';
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return s;
  if (d.getUTCFullYear() < 2000) return '';
  return s;
}

function parseIsoTime(iso) {
  const s = String(iso || '').trim();
  if (!s) return null;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return null;
  return d;
}

function isWithinMs(iso, ms) {
  const d = parseIsoTime(iso);
  if (!d) return false;
  const delta = Date.now() - d.getTime();
  return Number.isFinite(delta) && delta >= 0 && delta <= ms;
}

function isOlderThanMs(iso, ms) {
  const d = parseIsoTime(iso);
  if (!d) return false;
  const delta = Date.now() - d.getTime();
  return Number.isFinite(delta) && delta >= ms;
}

function formatNum(v, digits = 2) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(digits);
}

function safeJsonParse(s) {
  try {
    if (!s) return null;
    return JSON.parse(String(s));
  } catch (e) {
    return null;
  }
}

function safeJsonStringify(obj, maxLen = 900) {
  try {
    const s = JSON.stringify(obj, null, 2);
    if (!s) return '';
    if (s.length <= maxLen) return s;
    return `${s.slice(0, maxLen)}…`;
  } catch (e) {
    return '';
  }
}

function parseIsoMs(iso) {
  try {
    const d = new Date(String(iso || ''));
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  } catch (e) {
    return 0;
  }
}

function riskColor(risk) {
  const r = String(risk || '').toLowerCase();
  if (r === 'high') return '#ff7a7a';
  if (r === 'medium') return '#ffb74d';
  if (r === 'low') return '#9ef7c5';
  return '#9ea4d8';
}

export default function AgentDashboardPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { tokenAddress } = useParams();
  const tokenAddr = useMemo(() => String(tokenAddress || '').toLowerCase(), [tokenAddress]);

  const auth = useAuth();
  const canAwaken = Boolean(auth && auth.isAuthenticated && Array.isArray(auth.roles) && auth.roles.includes('agent_creation'));
  const sharedWalletAddress = auth && auth.userAddress ? String(auth.userAddress).trim() : '';

  const fundingDisabled = false;

  const apiBase = SERVER_NAME;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [awakenBusy, setAwakenBusy] = useState(false);
  const [awakenError, setAwakenError] = useState('');

  const latestDataRef = useRef(null);
  const latestActivitiesSigRef = useRef('');
  const autoFinalizeHandledRef = useRef('');

  const [fundingInfoLoading, setFundingInfoLoading] = useState(false);
  const [fundingInfoError, setFundingInfoError] = useState('');
  const [fundingInfo, setFundingInfo] = useState(null);

  const [fundingTxHash, setFundingTxHash] = useState('');
  const [funderAddress, setFunderAddress] = useState('');
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  const [finalizeError, setFinalizeError] = useState('');
  const [finalizeResult, setFinalizeResult] = useState(null);
  const [mintBusy, setMintBusy] = useState(false);
  const [mintError, setMintError] = useState('');
  const [receiptTokenId, setReceiptTokenId] = useState('');
  const [fundingWalletAddress, setFundingWalletAddress] = useState('');
  const [fundingWalletEthBalance, setFundingWalletEthBalance] = useState('');
  const [fundingWalletE3dBalance, setFundingWalletE3dBalance] = useState('');
  const [quickFundAmount, setQuickFundAmount] = useState('');
  const [quickFundMode, setQuickFundMode] = useState('e3d');
  const [quickFundBusy, setQuickFundBusy] = useState(false);
  const [quickFundError, setQuickFundError] = useState('');

  const [jobBusy, setJobBusy] = useState(false);
  const [jobError, setJobError] = useState('');
  const [jobId, setJobId] = useState('');
  const [jobData, setJobData] = useState(null);

  const [soulMdLoading, setSoulMdLoading] = useState(false);
  const [soulMdError, setSoulMdError] = useState('');
  const [soulMdText, setSoulMdText] = useState('');

  const [erc8004Loading, setErc8004Loading] = useState(false);
  const [erc8004Error, setErc8004Error] = useState('');
  const [erc8004TokenId, setErc8004TokenId] = useState('');
  const [openSections, setOpenSections] = useState({
    agentStatus: true,
    treasury: false,
    nextBestAction: true,
    artifacts: false,
    valueEvents: false,
    budgetPolicy: false,
    burnLedger: false,
    cooldownLedger: false,
    resourcePolicies: false,
    strategyPolicies: false,
    executionLedger: false,
    recommendedActions: false,
    soulMd: false,
    fundAgent: false,
  });

  const [recentWidthRatio, setRecentWidthRatio] = useState(() => {
    try {
      const v = window && window.localStorage ? window.localStorage.getItem('agentDashRecentWidthRatio') : '';
      const n = Number(v);
      if (Number.isFinite(n) && n > 0.15 && n < 0.75) return n;
    } catch (e) {
    }
    return 0.33;
  });
  const resizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartRatioRef = useRef(0);
  const splitRowRef = useRef(null);
  const splitWidthPxRef = useRef(0);
  const [splitWidthPx, setSplitWidthPx] = useState(0);
  const splitTopPxRef = useRef(0);
  const [splitTopPx, setSplitTopPx] = useState(0);
  const splitRoRef = useRef(null);
  const viewportHeightPxRef = useRef(0);
  const [viewportHeightPx, setViewportHeightPx] = useState(() => {
    try {
      return (window && window.innerHeight) ? Math.max(0, Math.floor(Number(window.innerHeight))) : 0;
    } catch (e) {
      return 0;
    }
  });

  const ensureSplitObserved = () => {
    try {
      if (splitRoRef.current) return;
      if (typeof ResizeObserver === 'undefined') return;
      if (!splitRowRef.current) return;
      splitRoRef.current = new ResizeObserver(() => {
        try {
          window.requestAnimationFrame(() => measureSplit());
        } catch (e) {
          measureSplit();
        }
      });
      splitRoRef.current.observe(splitRowRef.current);
    } catch (e) {
    }
  };

  const measureSplit = () => {
    try {
      try {
        const vh = (window && window.innerHeight) ? Math.max(0, Math.floor(Number(window.innerHeight))) : 0;
        if (vh !== viewportHeightPxRef.current) {
          viewportHeightPxRef.current = vh;
          setViewportHeightPx(vh);
        }
      } catch (e) {
      }

      const el = splitRowRef.current;
      if (!el || !el.getBoundingClientRect) return;
      const r = el.getBoundingClientRect();
      const w = Math.max(0, Math.floor(r.width));
      if (w !== splitWidthPxRef.current) {
        splitWidthPxRef.current = w;
        setSplitWidthPx(w);
      }

      const t = Math.max(0, Math.floor(r.top));
      if (t !== splitTopPxRef.current) {
        splitTopPxRef.current = t;
        setSplitTopPx(t);
      }
    } catch (e) {
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      try {
        if (!resizingRef.current) return;
        const x = e && e.clientX != null ? Number(e.clientX) : null;
        if (!Number.isFinite(x)) return;
        const w = splitWidthPxRef.current || splitWidthPx || 0;
        if (!w) return;

        const dx = resizeStartXRef.current - x;
        const pxDeltaAsRatio = dx / w;
        const nextRatio = resizeStartRatioRef.current + pxDeltaAsRatio;
        const clamped = Math.max(0.15, Math.min(0.75, nextRatio));
        setRecentWidthRatio(clamped);
      } catch (err) {
      }
    };

    const onUp = () => {
      try {
        if (!resizingRef.current) return;
        resizingRef.current = false;
        if (document && document.body) document.body.style.cursor = '';
        if (document && document.body) document.body.style.userSelect = '';
      } catch (err) {
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    try {
      if (window && window.localStorage) window.localStorage.setItem('agentDashRecentWidthRatio', String(recentWidthRatio));
    } catch (e) {
    }
  }, [recentWidthRatio]);

  useEffect(() => {
    let raf = 0;

    const schedule = () => {
      try {
        if (raf) return;
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          measureSplit();
        });
      } catch (e) {
        measureSplit();
      }
    };

    const ensureObserved = () => {
      ensureSplitObserved();
    };

    const onResize = () => {
      schedule();
      ensureObserved();
    };

    schedule();
    ensureObserved();
    try {
      window.requestAnimationFrame(() => {
        schedule();
        ensureObserved();
      });
    } catch (e) {
    }

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      try {
        if (splitRoRef.current) {
          splitRoRef.current.disconnect();
          splitRoRef.current = null;
        }
      } catch (e) {
      }
      try {
        if (raf) window.cancelAnimationFrame(raf);
      } catch (e) {
      }
    };
  }, []);

  useEffect(() => {
    try {
      if (loading) return;
      if (error) return;
      measureSplit();
      ensureSplitObserved();
      try {
        window.requestAnimationFrame(() => {
          measureSplit();
          ensureSplitObserved();
        });
      } catch (e) {
      }
    } catch (e) {
    }
  }, [loading, error]);

  const recentWidthPx = useMemo(() => {
    const w = splitWidthPx || 0;
    if (!w) return 420;
    const desired = Math.floor(w * recentWidthRatio);
    return Math.max(260, Math.min(900, desired));
  }, [splitWidthPx, recentWidthRatio]);

  const stickyTopPx = 16;

  const recentHeightPx = useMemo(() => {
    try {
      const vh = Number.isFinite(viewportHeightPx) ? Number(viewportHeightPx) : 0;
      const top = Number.isFinite(splitTopPx) ? splitTopPx : 0;
      const bottomPad = 56;
      const raw = Math.floor(vh - top - stickyTopPx - bottomPad);
      return Math.max(180, raw);
    } catch (e) {
      return 600;
    }
  }, [splitTopPx, viewportHeightPx]);

  const fetchAgent = async (mountedRef) => {
    const mounted = mountedRef;
    try {
      setLoading(true);
      setError('');
      const resp = await axios.get(`${apiBase}/agents/${tokenAddr}`, { params: { limit: 50 } });
      if (!mounted.current) return;
      setData(resp && resp.data ? resp.data : null);
    } catch (e) {
      if (!mounted.current) return;
      setError('Failed to load agent');
    } finally {
      if (!mounted.current) return;
      setLoading(false);
    }
  };

  const fetchFundingInfo = async (mountedRef) => {
    const mounted = mountedRef;
    try {
      setFundingInfoLoading(true);
      setFundingInfoError('');
      const resp = await axios.get(`${apiBase}/agents/${tokenAddr}/funding_info`);
      if (!mounted.current) return;
      setFundingInfo(resp && resp.data ? resp.data : null);
    } catch (e) {
      if (!mounted.current) return;
      const status = e && e.response && e.response.status != null ? String(e.response.status) : '';
      const data = e && e.response ? e.response.data : null;
      const serverMsg = data && typeof data === 'object' && data.message ? String(data.message) : '';
      const bodyText = data && typeof data === 'string' ? data : '';
      const detail = status ? ` (${status})` : '';
      const msg = serverMsg
        ? `${serverMsg}${detail}`
        : (bodyText ? `Failed to load funding info${detail}: ${bodyText.slice(0, 180)}` : `Failed to load funding info${detail}`);
      setFundingInfoError(msg);
    } finally {
      if (!mounted.current) return;
      setFundingInfoLoading(false);
    }
  };

  useEffect(() => {
    const mounted = { current: true };
    fetchAgent(mounted);
    if (!fundingDisabled) fetchFundingInfo(mounted);
    return () => { mounted.current = false; };
  }, [tokenAddr, apiBase, fundingDisabled]);

  useEffect(() => {
    try {
      latestDataRef.current = data;
      const acts = data && Array.isArray(data.activities) ? data.activities : [];
      if (!acts.length) {
        latestActivitiesSigRef.current = '0';
        return;
      }
      const a0 = acts[0] || {};
      const sig = `${acts.length}|${String(a0.ts || '')}|${String(a0.type || '')}|${String(a0.summary || '')}`;
      latestActivitiesSigRef.current = sig;
    } catch (e) {
    }
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        if (cancelled) return;
        if (!tokenAddr || !/^0x[a-fA-F0-9]{40}$/.test(tokenAddr)) return;
        if (loading) return;
        if (error) return;

        const resp = await axios.get(`${apiBase}/agents/${tokenAddr}`, { params: { limit: 50 } });
        if (cancelled) return;
        const next = resp && resp.data ? resp.data : null;
        const acts = next && Array.isArray(next.activities) ? next.activities : [];
        const prevSig = latestActivitiesSigRef.current || '';

        let nextSig = '0';
        if (acts.length) {
          const a0 = acts[0] || {};
          nextSig = `${acts.length}|${String(a0.ts || '')}|${String(a0.type || '')}|${String(a0.summary || '')}`;
        }

        if (nextSig && nextSig !== prevSig) {
          setData(next);
        }
      } catch (e) {
        // Background poll: ignore failures to avoid UI jitter.
      }
    };

    const schedule = () => {
      try {
        if (cancelled) return;
        timer = window.setTimeout(async () => {
          await tick();
          schedule();
        }, 5000);
      } catch (e) {
      }
    };

    // Kick off after initial load, but don't require waiting 5s for the first update.
    try {
      window.setTimeout(() => { tick(); }, 5000);
    } catch (e) {
    }
    schedule();

    return () => {
      cancelled = true;
      try {
        if (timer) window.clearTimeout(timer);
      } catch (e) {
      }
    };
  }, [tokenAddr, apiBase, loading, error]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setErc8004Loading(true);
        setErc8004Error('');
        setErc8004TokenId('');

        if (!/^0x[a-fA-F0-9]{40}$/.test(tokenAddr)) return;
        if (!E3D_NFT_MANAGER_ADDRESS || !/^0x[a-fA-F0-9]{40}$/.test(String(E3D_NFT_MANAGER_ADDRESS))) return;

        const rpcUrls = [
          'https://mainnet.infura.io/v3/70518d4d4d50487c88fbe3221f52d62d',
          'https://cloudflare-eth.com',
          'https://eth.llamarpc.com',
        ];

        const erc8004Abi = [
          'function tokenToAgentNFT(address tokenAddress) view returns (uint256)',
        ];

        const callWithProvider = async (provider) => {
          const c = new ethers.Contract(E3D_NFT_MANAGER_ADDRESS, erc8004Abi, provider);
          return c.tokenToAgentNFT(tokenAddr);
        };

        let provider = null;
        try {
          if (window.ethereum) {
            const web3 = new ethers.providers.Web3Provider(window.ethereum, 'any');
            const net = await web3.getNetwork().catch(() => null);
            if (net && Number(net.chainId) === 1) provider = web3;
          }
        } catch (e) {
        }

        let tokenIdBn;
        if (provider) {
          try {
            tokenIdBn = await callWithProvider(provider);
          } catch (e1) {
            tokenIdBn = null;
          }
        }

        if (!tokenIdBn) {
          let lastErr = null;
          for (const url of rpcUrls) {
            try {
              const p = new ethers.providers.StaticJsonRpcProvider(url, {
                chainId: 1,
                name: 'homestead',
              });
              tokenIdBn = await callWithProvider(p);
              lastErr = null;
              break;
            } catch (e2) {
              lastErr = e2;
            }
          }
          if (!tokenIdBn) throw lastErr || new Error('ERC-8004 lookup failed');
        }

        const tokenId = tokenIdBn && tokenIdBn.toString ? tokenIdBn.toString() : '';
        if (cancelled) return;

        if (tokenId && tokenId !== '0') setErc8004TokenId(tokenId);
      } catch (e) {
        if (cancelled) return;
        try {
          console.error('ERC-8004 status check failed:', e);
        } catch (e2) {
        }
        setErc8004Error('');
        setErc8004TokenId('');
      } finally {
        if (cancelled) return;
        setErc8004Loading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [tokenAddr]);

  const handleAwaken = async () => {
    try {
      setAwakenBusy(true);
      setAwakenError('');
      if (fundingDisabled) {
        setAwakenError('Awakening is disabled in Token Agent Monitor Preview.');
        return;
      }
      if (!canAwaken) {
        setAwakenError('Your account is not permitted to awaken agents.');
        return;
      }
      await axios.post(`${apiBase}/agents/${tokenAddr}/awaken`, {}, { withCredentials: true });
      const mounted = { current: true };
      await fetchAgent(mounted);
      mounted.current = false;
    } catch (e) {
      setAwakenError('Awaken failed');
    } finally {
      setAwakenBusy(false);
    }
  };

  const fetchJob = async (id) => {
    const resp = await axios.get(`${apiBase}/agent_creation_jobs/${String(id)}`, { withCredentials: true });
    return resp && resp.data ? resp.data : null;
  };

  const startPollingJob = async (id) => {
    const jid = String(id || '').trim();
    if (!jid) return;
    setJobId(jid);

    let stopped = false;
    const pollOnce = async () => {
      try {
        const d = await fetchJob(jid);
        const j = d && d.job ? d.job : null;
        if (j) setJobData(j);
        const status = j && j.status ? String(j.status) : '';
        if (status === 'ready' || status === 'failed') return true;
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message
          ? String(e.response.data.message)
          : 'Failed to load job status';
        setJobError(msg);
        return false;
      }
      return false;
    };

    await pollOnce();
    const t = setInterval(async () => {
      if (stopped) return;
      const done = await pollOnce();
      if (done) {
        stopped = true;
        clearInterval(t);
      }
    }, 3_000);

    return () => {
      stopped = true;
      clearInterval(t);
    };
  };

  const requestAgentCreation = async (fundingHash) => {
    const h = String(fundingHash || '').trim();
    if (!h) throw new Error('Missing funding tx hash');

    const resp = await axios.post(
      `${apiBase}/agents/${tokenAddr}/request_agent_creation`,
      { fundingTxHash: h },
      { withCredentials: true }
    );
    return resp && resp.data ? resp.data : null;
  };

  const token = data && data.token ? data.token : null;
  const agent = data && data.agent ? data.agent : null;
  const activities = data && Array.isArray(data.activities) ? data.activities : [];
  const fundingStats = data && data.funding_stats ? data.funding_stats : null;
  const fundingHistory = data && Array.isArray(data.funding_history) ? data.funding_history : [];
  const treasury = data && data.treasury ? data.treasury : null;
  const budgetPolicy = data && data.budget_policy ? data.budget_policy : null;
  const burnStats = data && data.burn_stats ? data.burn_stats : null;
  const burns = data && Array.isArray(data.burns) ? data.burns : [];
  const resourcePolicies = data && Array.isArray(data.resource_policies) ? data.resource_policies : [];
  const strategies = data && Array.isArray(data.strategies) ? data.strategies : [];
  const executions = data && Array.isArray(data.executions) ? data.executions : [];
  const artifacts = data && Array.isArray(data.artifacts) ? data.artifacts : [];
  const valueEvents = data && Array.isArray(data.value_events) ? data.value_events : [];
  const cooldowns = data && Array.isArray(data.cooldowns) ? data.cooldowns : [];
  const nextBestAction = data && data.next_best_action && typeof data.next_best_action === 'object' ? data.next_best_action : null;
  const orchestratorSummary = data && data.orchestrator_summary && typeof data.orchestrator_summary === 'object'
    ? data.orchestrator_summary
    : null;

  const recommendationsInfo = useMemo(() => {
    const allowed = new Set([
      'publish_daily_brief',
      'publish_weekly_recap',
      'raise_alert',
      'request_data_pull',
      'propose_campaign',
    ]);

    const bundles = [];
    const parseErrors = [];
    (Array.isArray(activities) ? activities : []).forEach((a) => {
      if (!a || String(a.type || '') !== 'openclaw_recommendations_v1') return;
      const ev = safeJsonParse(a.evidence_json);
      if (!ev || typeof ev !== 'object') {
        parseErrors.push({ ts: a.ts || '', reason: 'invalid_json' });
        return;
      }
      const recs = Array.isArray(ev.recommendations) ? ev.recommendations : [];
      bundles.push({
        ts: String(a.ts || ''),
        summary: String(a.summary || ''),
        run_id: ev.run_id != null ? String(ev.run_id) : '',
        generated_at: ev.generated_at != null ? String(ev.generated_at) : '',
        schema_version: ev.schema_version != null ? Number(ev.schema_version) : null,
        inputs: ev.inputs != null ? ev.inputs : null,
        recommendations: recs.map((r, idx) => {
          const actionType = r && r.action_type != null ? String(r.action_type) : '';
          return {
            id: r && r.id != null ? String(r.id) : (r && r.title ? String(r.title) : `rec_${idx + 1}`),
            action_type: actionType,
            action_type_allowed: actionType ? allowed.has(actionType) : false,
            title: r && r.title != null ? String(r.title) : (actionType || 'recommendation'),
            rationale: r && r.rationale != null ? String(r.rationale) : '',
            confidence: r && r.confidence != null ? Number(r.confidence) : null,
            risk_level: r && r.risk_level != null ? String(r.risk_level) : '',
            expires_at: r && r.expires_at != null ? String(r.expires_at) : '',
            dependencies: r && Array.isArray(r.dependencies) ? r.dependencies.map((x) => String(x)) : [],
            params: r && r.params != null ? r.params : null,
            evidence_links: r && Array.isArray(r.evidence_links) ? r.evidence_links.map((x) => String(x)).filter(Boolean) : [],
          };
        }),
      });
    });

    bundles.sort((a, b) => parseIsoMs(b.generated_at || b.ts) - parseIsoMs(a.generated_at || a.ts));
    const latest = bundles.length ? bundles[0] : null;
    const recs = latest && Array.isArray(latest.recommendations) ? latest.recommendations : [];
    const unknownCount = recs.filter((r) => r && r.action_type && !r.action_type_allowed).length;
    return {
      latest,
      recommendations: recs,
      parseErrors,
      unknownCount,
    };
  }, [activities]);

  const hasAwakenedActivity = useMemo(() => {
    return activities.some((a) => {
      const t = a && a.type ? String(a.type) : '';
      return t === 'agent_awakened';
    });
  }, [activities]);

  const isAwakened = Boolean(
    (agent && agent.status && String(agent.status).toLowerCase() !== 'dormant') || hasAwakenedActivity
  );

  const icon = (token && (token.icon || token.icon2)) ? (token.icon || token.icon2) : genericTokenIcon;
  const title = (token && (token.name || token.symbol)) ? `${token.name || ''}${token.symbol ? ` (${token.symbol})` : ''}`.trim() : tokenAddr;
  const pageTitle = `${title} — Token Agent Monitor Preview`;

  const escrowAddr = fundingInfo && fundingInfo.escrow_address ? String(fundingInfo.escrow_address) : '';
  const e3dTokenAddr = fundingInfo && fundingInfo.e3d_token_address ? String(fundingInfo.e3d_token_address) : '';
  const receiptMgrAddr = fundingInfo && fundingInfo.receipt_nft_manager ? String(fundingInfo.receipt_nft_manager) : '';

  const displayState = useMemo(() => {
    const s = agent && agent.status ? String(agent.status) : 'dormant';
    if (String(s).toLowerCase() === 'dormant' && hasAwakenedActivity) return 'awakened';
    return s;
  }, [agent, hasAwakenedActivity]);

  const displayHeartbeat = useMemo(() => {
    return agent ? formatHeartbeat(agent.last_heartbeat_iso || '') : '';
  }, [agent]);

  const scoreInfo = useMemo(() => {
    const e24 = Number(fundingStats && fundingStats.credited_e3d_24h != null ? fundingStats.credited_e3d_24h : 0);
    const e7 = Number(fundingStats && fundingStats.credited_e3d_7d != null ? fundingStats.credited_e3d_7d : 0);
    const funders = Number(fundingStats && fundingStats.funders_count != null ? fundingStats.funders_count : 0);
    const total = Number(fundingStats && fundingStats.total_credited_e3d != null ? fundingStats.total_credited_e3d : 0);

    const e24n = Number.isFinite(e24) ? e24 : 0;
    const e7n = Number.isFinite(e7) ? e7 : 0;
    const fundersN = Number.isFinite(funders) ? funders : 0;
    const totalN = Number.isFinite(total) ? total : 0;

    const score = e7n + (0.5 * e24n) + (10 * fundersN);
    const lastFundedIso = normalizeDisplayIso(fundingStats && fundingStats.last_funded_iso ? String(fundingStats.last_funded_iso) : '');

    return {
      e24: e24n,
      e7: e7n,
      funders: fundersN,
      total: totalN,
      score,
      lastFundedIso,
    };
  }, [fundingStats]);

  const badges = useMemo(() => {
    const out = [];
    const st = String(displayState || '').toLowerCase();
    const live = st === 'running';
    if (live) out.push({ label: 'Live', bg: '#4caf50' });

    if (erc8004TokenId) out.push({ label: 'ERC-8004', bg: '#009688', kind: 'erc8004' });

    if (Number.isFinite(scoreInfo.e24) && scoreInfo.e24 > 0) out.push({ label: 'Hot', bg: '#ff5722' });
    if (Number.isFinite(scoreInfo.total) && scoreInfo.total >= 1000) out.push({ label: 'Whale', bg: '#7c4dff' });

    if (scoreInfo.lastFundedIso && isWithinMs(scoreInfo.lastFundedIso, 24 * 60 * 60 * 1000)) {
      out.push({ label: 'New', bg: '#03a9f4' });
    }
    if (Number.isFinite(scoreInfo.e24) && scoreInfo.e24 > 0 && Number.isFinite(scoreInfo.e7) && scoreInfo.e7 > 0) {
      out.push({ label: 'Streak', bg: '#ff9800' });
    }
    if (agent && agent.last_heartbeat_iso && isOlderThanMs(agent.last_heartbeat_iso, 24 * 60 * 60 * 1000)) {
      out.push({ label: 'Sleeping', bg: '#607d8b' });
    }

    return out;
  }, [agent, displayState, scoreInfo, erc8004TokenId]);

  const tokenAddressUrl = useMemo(() => toEtherscanTokenUrl(tokenAddr), [tokenAddr]);
  const escrowAddressUrl = useMemo(() => toEtherscanAddressUrl(escrowAddr), [escrowAddr]);
  const receiptMgrUrl = useMemo(() => toEtherscanAddressUrl(receiptMgrAddr), [receiptMgrAddr]);
  const fundingTxUrl = useMemo(() => toEtherscanTxUrl(fundingTxHash), [fundingTxHash]);
  const erc8004NftUrl = useMemo(() => toEtherscanNftTokenUrl(E3D_NFT_MANAGER_ADDRESS, erc8004TokenId), [erc8004TokenId]);
  const fundingWalletAddressUrl = useMemo(() => toEtherscanAddressUrl(fundingWalletAddress), [fundingWalletAddress]);
  const fundingWalletEthBalanceDisplay = useMemo(() => {
    const n = Number(fundingWalletEthBalance);
    if (!Number.isFinite(n)) return fundingWalletEthBalance ? String(fundingWalletEthBalance) : '';
    return n.toFixed(4);
  }, [fundingWalletEthBalance]);
  const fundingWalletE3dBalanceDisplay = useMemo(() => {
    const n = Number(fundingWalletE3dBalance);
    if (!Number.isFinite(n)) return fundingWalletE3dBalance ? String(fundingWalletE3dBalance) : '';
    return n.toFixed(4);
  }, [fundingWalletE3dBalance]);
  const fundingWalletBalanceLabel = quickFundMode === 'eth' ? 'Wallet ETH' : 'Wallet E3D';
  const fundingWalletBalanceDisplay = quickFundMode === 'eth' ? fundingWalletEthBalanceDisplay : fundingWalletE3dBalanceDisplay;
  const fundingLadderRows = useMemo(() => {
    const ladder = nextBestAction && nextBestAction.funding_ladder && typeof nextBestAction.funding_ladder === 'object' && !Array.isArray(nextBestAction.funding_ladder)
      ? nextBestAction.funding_ladder
      : {};
    const out = [];
    Object.entries(ladder).forEach(([rawKey, value], idx) => {
      const key = String(rawKey || '').trim();
      if (!key) return;
      if (Number.isFinite(Number(value))) {
        const amount = Number(value);
        if (amount > 0) {
          out.push({
            key,
            label: key.replace(/_/g, ' '),
            amount_e3d: amount,
            note: '',
            sort: amount,
            idx,
          });
        }
        return;
      }
      if (!value || typeof value !== 'object' || Array.isArray(value)) return;
      const amount = Number(
        value.amount_e3d != null
          ? value.amount_e3d
          : (value.required_e3d != null ? value.required_e3d : value.target_e3d)
      );
      if (!Number.isFinite(amount) || amount <= 0) return;
      const note = value.note != null
        ? String(value.note)
        : (value.description != null ? String(value.description) : '');
      out.push({
        key,
        label: value.label != null ? String(value.label) : key.replace(/_/g, ' '),
        amount_e3d: amount,
        note,
        sort: amount,
        idx,
      });
    });
    out.sort((a, b) => {
      if (a.sort !== b.sort) return a.sort - b.sort;
      return a.idx - b.idx;
    });
    return out.slice(0, 8);
  }, [nextBestAction]);
  const fundingLadderHint = useMemo(() => {
    const amount = Number(String(quickFundAmount || '').trim());
    if (!Number.isFinite(amount) || amount <= 0) return '';
    if (!fundingLadderRows.length) return '';
    const reached = fundingLadderRows.filter((row) => amount >= row.amount_e3d);
    if (reached.length) {
      const best = reached[reached.length - 1];
      return `Meets \"${best.label}\" ladder target (${formatNum(best.amount_e3d, 2)} E3D).`;
    }
    const next = fundingLadderRows[0];
    return `Below \"${next.label}\" ladder target (${formatNum(next.amount_e3d, 2)} E3D).`;
  }, [quickFundAmount, fundingLadderRows]);
  const fundingEstimate = useMemo(() => {
    const amount = Number(String(quickFundAmount || '').trim());
    const burn24h = Number(burnStats && burnStats.burned_e3d_24h != null ? burnStats.burned_e3d_24h : 0);
    const burn7d = Number(burnStats && burnStats.burned_e3d_7d != null ? burnStats.burned_e3d_7d : 0);
    const executions7d = Number(burnStats && burnStats.execution_count != null ? burnStats.execution_count : 0);
    const avgBurnPerTurn = Number.isFinite(executions7d) && executions7d > 0 && Number.isFinite(burn7d)
      ? (burn7d / executions7d)
      : 0;
    const burnPerDay = Number.isFinite(burn24h) && burn24h > 0
      ? burn24h
      : (Number.isFinite(burn7d) && burn7d > 0 ? (burn7d / 7) : 0);

    if (!Number.isFinite(amount) || amount <= 0) {
      return {
        amount: 0,
        turns: null,
        days: null,
        burnPerDay,
        avgBurnPerTurn,
      };
    }

    return {
      amount,
      turns: avgBurnPerTurn > 0 ? (amount / avgBurnPerTurn) : null,
      days: burnPerDay > 0 ? (amount / burnPerDay) : null,
      burnPerDay,
      avgBurnPerTurn,
    };
  }, [quickFundAmount, burnStats]);
  const fundingEstimateSummary = useMemo(() => {
    if (!fundingEstimate || !Number.isFinite(fundingEstimate.amount) || fundingEstimate.amount <= 0) return '';
    const parts = [];
    if (fundingEstimate.turns != null && Number.isFinite(fundingEstimate.turns)) {
      parts.push(`~${formatNum(fundingEstimate.turns, fundingEstimate.turns >= 10 ? 0 : 1)} turns`);
    }
    if (fundingEstimate.days != null && Number.isFinite(fundingEstimate.days)) {
      parts.push(`~${formatNum(fundingEstimate.days, fundingEstimate.days >= 10 ? 0 : 1)} days of runway`);
    }
    if (!parts.length) return 'Need a little burn history before I can estimate funding duration.';
    return parts.join(' • ');
  }, [fundingEstimate]);
  const valueEventsTotalEstimate = useMemo(() => {
    return (Array.isArray(valueEvents) ? valueEvents : []).reduce((sum, item) => {
      const n = Number(item && item.estimated_value != null ? item.estimated_value : 0);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [valueEvents]);
  const swapReturnInfo = useMemo(() => {
    try {
      const params = new URLSearchParams(location && location.search ? location.search : '');
      const txHash = String(params.get('swapTxHash') || '').trim();
      const funder = String(params.get('funderAddress') || '').trim();
      return { txHash, funder };
    } catch (e) {
      return { txHash: '', funder: '' };
    }
  }, [location]);

  const soulMdIpfs = agent && agent.soul_md_hash ? String(agent.soul_md_hash) : '';
  const soulMdHttp = useMemo(() => ipfsToHttpUrl(soulMdIpfs), [soulMdIpfs]);
  const soulMdProxyUrl = useMemo(() => {
    if (!soulMdIpfs) return '';
    return `${apiBase}/api/ipfs_text?uri=${encodeURIComponent(soulMdIpfs)}`;
  }, [apiBase, soulMdIpfs]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!soulMdIpfs) {
          setSoulMdText('');
          setSoulMdError('');
          return;
        }
        setSoulMdLoading(true);
        setSoulMdError('');
        const resp = await axios.get(soulMdProxyUrl, {
          responseType: 'text',
          transformResponse: [(d) => d],
        });
        if (cancelled) return;
        setSoulMdText(String(resp && resp.data ? resp.data : ''));
      } catch (e) {
        if (cancelled) return;
        const status = e && e.response && e.response.status != null ? String(e.response.status) : '';
        const msg = e && e.response && e.response.data && e.response.data.message
          ? String(e.response.data.message)
          : (e && e.message ? String(e.message) : '');
        const detail = status ? ` (${status})` : '';
        setSoulMdError(`Failed to load soul.md${detail}${msg ? `: ${msg}` : ''}`);
        setSoulMdText('');
      } finally {
        if (cancelled) return;
        setSoulMdLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [soulMdIpfs, soulMdProxyUrl]);

  const resetFundingWalletState = () => {
    setFundingWalletAddress('');
    setFundingWalletEthBalance('');
    setFundingWalletE3dBalance('');
  };

  const syncFundingWallet = async (requestAccess = false) => {
    try {
      if (!window.ethereum) {
        resetFundingWalletState();
        return null;
      }

      if (!requestAccess && !sharedWalletAddress) {
        resetFundingWalletState();
        return null;
      }

      const method = requestAccess ? 'eth_requestAccounts' : 'eth_accounts';
      const accounts = await window.ethereum.request({ method });
      const acct = Array.isArray(accounts) && accounts[0] ? ethers.utils.getAddress(String(accounts[0])) : '';
      if (!acct) {
        resetFundingWalletState();
        return null;
      }

      if (!requestAccess && sharedWalletAddress) {
        try {
          const normalizedShared = ethers.utils.getAddress(sharedWalletAddress);
          if (normalizedShared !== acct) {
            resetFundingWalletState();
            return null;
          }
        } catch (e) {
          resetFundingWalletState();
          return null;
        }
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const rawEthBal = await provider.getBalance(acct);
      setFundingWalletAddress(acct);
      setFundingWalletEthBalance(ethers.utils.formatEther(rawEthBal));
      setFunderAddress((prev) => {
        const p = String(prev || '').trim();
        return p || acct;
      });

      if (!/^0x[a-fA-F0-9]{40}$/.test(String(e3dTokenAddr || '').trim())) {
        setFundingWalletE3dBalance('');
        return { walletAddress: acct, provider, decimals: 18 };
      }

      const token = new ethers.Contract(String(e3dTokenAddr).trim(), E3D_ERC20_MIN_ABI, provider);
      let decimals = 18;
      try {
        decimals = Number(await token.decimals());
      } catch (e) {
      }
      const rawBal = await token.balanceOf(acct);
      setFundingWalletE3dBalance(ethers.utils.formatUnits(rawBal, decimals));
      return { walletAddress: acct, provider, decimals };
    } catch (e) {
      if (requestAccess) {
        setQuickFundError(e && e.message ? String(e.message) : 'Failed to connect wallet');
      }
      if (!requestAccess) {
        resetFundingWalletState();
      }
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      if (!fundingInfo) return;
      await syncFundingWallet(false);
    };
    run();
    return () => { cancelled = true; };
  }, [fundingInfo, e3dTokenAddr, sharedWalletAddress]);

  useEffect(() => {
    if (!sharedWalletAddress) {
      resetFundingWalletState();
    }
  }, [sharedWalletAddress]);

  useEffect(() => {
    if (!window.ethereum || !window.ethereum.on || !window.ethereum.removeListener) return undefined;
    const handleAccountsChanged = () => {
      syncFundingWallet(false);
    };
    const handleChainChanged = () => {
      syncFundingWallet(false);
    };
    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);
    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [fundingInfo, e3dTokenAddr, sharedWalletAddress]);

  const handleFinalizeFunding = async (txHashOverride = '', funderAddressOverride = '') => {
    try {
      setFinalizeBusy(true);
      setFinalizeError('');
      setFinalizeResult(null);
      setReceiptTokenId('');
      setMintError('');
      setJobBusy(false);
      setJobError('');
      setJobId('');
      setJobData(null);

      const txHash = String(txHashOverride || fundingTxHash || '').trim();
      if (!txHash) {
        setFinalizeError('Missing funding transaction hash');
        return;
      }
      if (txHashOverride) setFundingTxHash(txHash);

      const payload = {
        txHash,
      };
      const fa = String(funderAddressOverride || funderAddress || '').trim();
      if (fa) payload.funderAddress = fa;

      const resp = await axios.post(`${apiBase}/agents/${tokenAddr}/finalize_funding`, payload, { withCredentials: true });
      const fr = resp && resp.data ? resp.data : null;
      setFinalizeResult(fr);

      if (fr && fr.pending) {
        return;
      }

      if (fr && fr.ok) {
        setJobBusy(true);
        try {
          const r = await requestAgentCreation(txHash);
          const jid = r && r.jobId ? String(r.jobId) : '';
          if (jid) await startPollingJob(jid);
        } catch (e) {
          const msg = e && e.response && e.response.data && e.response.data.message
            ? String(e.response.data.message)
            : 'Failed to request agent creation';
          setJobError(msg);
        } finally {
          setJobBusy(false);
        }
      }

      const mounted = { current: true };
      await fetchAgent(mounted);
      await fetchFundingInfo(mounted);
      mounted.current = false;
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message
        ? String(e.response.data.message)
        : 'Finalize failed';
      setFinalizeError(msg);
    } finally {
      setFinalizeBusy(false);
    }
  };

  const handleMintReceipt = async () => {
    try {
      setMintBusy(true);
      setMintError('');

      const uri = finalizeResult && finalizeResult.receipt_metadata_uri ? String(finalizeResult.receipt_metadata_uri) : '';
      if (!uri) {
        setMintError('Missing receipt metadata URI');
        return;
      }

      if (!window.ethereum) {
        setMintError('MetaMask is not installed');
        return;
      }

      await window.ethereum.request({ method: 'eth_requestAccounts' });
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(E3D_NFT_MANAGER_ADDRESS, NFTManagerABI, signer);

      const tx = await nftContract.mintNFT(uri, [tokenAddr]);
      const receipt = await tx.wait();
      const transferEvent = receipt && receipt.events
        ? receipt.events.find((ev) => ev && ev.event === 'Transfer' && ev.args && ev.args.tokenId != null)
        : null;
      const tokenId = transferEvent && transferEvent.args && transferEvent.args.tokenId != null
        ? String(transferEvent.args.tokenId)
        : '';
      setReceiptTokenId(tokenId);

      const fundingHash = String(fundingTxHash || '').trim();
      if (fundingHash && tokenId) {
        try {
          await axios.post(`${apiBase}/agents/${tokenAddr}/store_receipt`, {
            txHash: fundingHash,
            receiptTokenId: tokenId,
          });
        } catch (e) {
          // Persisting receipt is best-effort; mint already succeeded.
          const msg = e && e.response && e.response.data && e.response.data.message
            ? String(e.response.data.message)
            : 'Failed to store receipt';
          setMintError(msg);
        }
      }
    } catch (e) {
      setMintError(e && e.message ? String(e.message) : 'Mint receipt failed');
    } finally {
      setMintBusy(false);
    }
  };

  const handleQuickFund = async () => {
    try {
      setQuickFundBusy(true);
      setQuickFundError('');
      setFinalizeError('');
      setMintError('');

      const amountText = String(quickFundAmount || '').trim();
      if (!amountText) {
        setQuickFundError('Enter an E3D amount');
        return;
      }

      const amountNum = Number(amountText);
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        setQuickFundError('Enter a valid E3D amount');
        return;
      }

      if (!window.ethereum) {
        setQuickFundError('MetaMask is not installed');
        return;
      }

      if (!/^0x[a-fA-F0-9]{40}$/.test(String(escrowAddr || '').trim())) {
        setQuickFundError('Escrow address unavailable');
        return;
      }

      if (!/^0x[a-fA-F0-9]{40}$/.test(String(e3dTokenAddr || '').trim())) {
        setQuickFundError('E3D token address unavailable');
        return;
      }

      const walletInfo = await syncFundingWallet(true);
      if (!walletInfo || !walletInfo.walletAddress || !walletInfo.provider) {
        setQuickFundError('Connect your wallet from the header to continue');
        return;
      }

      const signer = walletInfo.provider.getSigner();
      const token = new ethers.Contract(String(e3dTokenAddr).trim(), E3D_ERC20_MIN_ABI, signer);
      const decimals = Number.isFinite(walletInfo.decimals) ? walletInfo.decimals : 18;
      const amountBase = ethers.utils.parseUnits(amountText, decimals);
      const rawBal = await token.balanceOf(walletInfo.walletAddress);
      if (rawBal.lt(amountBase)) {
        setQuickFundError('Insufficient E3D balance in connected wallet');
        return;
      }

      const tx = await token.transfer(String(escrowAddr).trim(), amountBase);
      setFundingTxHash(tx.hash);
      setFunderAddress(walletInfo.walletAddress);

      const receipt = await tx.wait();
      if (!receipt || receipt.status !== 1) {
        setQuickFundError('Funding transaction failed');
        return;
      }

      await handleFinalizeFunding(tx.hash, walletInfo.walletAddress);
      await syncFundingWallet(false);
    } catch (e) {
      setQuickFundError(e && e.message ? String(e.message) : 'Failed to fund with E3D');
    } finally {
      setQuickFundBusy(false);
    }
  };

  useEffect(() => {
    const txHash = String(swapReturnInfo && swapReturnInfo.txHash ? swapReturnInfo.txHash : '').trim();
    if (!txHash) return;
    if (autoFinalizeHandledRef.current === txHash) return;
    autoFinalizeHandledRef.current = txHash;

    setOpenSections((prev) => ({
      ...prev,
      fundAgent: true,
    }));

    if (swapReturnInfo && swapReturnInfo.funder) {
      setFunderAddress(String(swapReturnInfo.funder));
    }
    setFundingTxHash(txHash);

    let cancelled = false;
    const run = async () => {
      try {
        if (!cancelled) {
          await handleFinalizeFunding(txHash, swapReturnInfo && swapReturnInfo.funder ? String(swapReturnInfo.funder) : '');
        }
      } finally {
        if (!cancelled) {
          navigate(location.pathname, { replace: true });
        }
      }
    };
    run();

    return () => { cancelled = true; };
  }, [swapReturnInfo, navigate, location.pathname]);

  const toggleSection = (sectionKey) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const renderSectionHeader = (sectionKey, title, rightContent = null) => {
    const isOpen = !!openSections[sectionKey];
    const onActivate = () => toggleSection(sectionKey);
    const onKeyDown = (e) => {
      if (!e) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    };

    return (
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        className={classes.sectionHeader}
        onClick={onActivate}
        onKeyDown={onKeyDown}
      >
        <Box className={classes.sectionHeaderMain}>
          <Box className={classes.sectionArrow} style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</Box>
          <Typography variant="h6" style={{ color: 'white', marginBottom: 0, textAlign: 'left' }}>{title}</Typography>
        </Box>
        {rightContent ? <Box onClick={(e) => e.stopPropagation()}>{rightContent}</Box> : null}
      </Box>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.heroTitleRow}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
            <img
              src={icon}
              alt=""
              style={{ width: 36, height: 36, borderRadius: 18, background: '#000', flexShrink: 0 }}
              onError={(e) => { e.currentTarget.src = genericTokenIcon; }}
            />
            <Box style={{ minWidth: 0 }}>
              <Typography className={classes.heroTitle}>{pageTitle}</Typography>
              {tokenAddressUrl ? (
                <Link href={tokenAddressUrl} target="_blank" rel="noopener noreferrer" className={classes.heroAddr} style={{ color: '#4d79ff', display: 'block' }}>
                  {tokenAddr}
                </Link>
              ) : (
                <div className={classes.heroAddr}>{tokenAddr}</div>
              )}
              {!!badges.length && (
                <Box style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                {badges.slice(0, 6).map((b) => (
                  b && b.kind === 'erc8004' ? (
                    <Tooltip
                      key={b.label}
                      title={<span style={{ whiteSpace: 'pre-line' }}>{`ERC-8004 identity registered\nToken ID: ${erc8004TokenId || ''}`}</span>}
                    >
                      {erc8004NftUrl ? (
                        <Link href={erc8004NftUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <Box
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              padding: '2px 6px',
                              borderRadius: 999,
                              background: b.bg,
                              color: 'white',
                            }}
                          >
                            {b.label}
                          </Box>
                        </Link>
                      ) : (
                        <Box
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            padding: '2px 6px',
                            borderRadius: 999,
                            background: b.bg,
                            color: 'white',
                          }}
                        >
                          {b.label}
                        </Box>
                      )}
                    </Tooltip>
                  ) : (
                    <Box
                      key={b.label}
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        padding: '2px 6px',
                        borderRadius: 999,
                        background: b.bg,
                        color: 'white',
                      }}
                    >
                      {b.label}
                    </Box>
                  )
                ))}
                </Box>
              )}
              {!!erc8004Error && (
                <Typography style={{ color: '#ff7a7a', fontSize: 12, marginTop: 4 }}>{erc8004Error}</Typography>
              )}
            </Box>
          </Box>
          <div className={classes.heroNav}>
            <Button
              variant="contained"
              size="small"
              className={classes.awakenButton}
              disabled={fundingDisabled || !canAwaken || awakenBusy || loading || isAwakened}
              onClick={handleAwaken}
              style={{
                backgroundColor: isAwakened ? 'rgba(255,255,255,0.08)' : '#4d79ff',
                color: isAwakened ? '#9ea4d8' : '#fff',
              }}
            >
              {awakenBusy ? 'Awakening…' : (isAwakened ? 'Awakened' : 'Awaken')}
            </Button>
            <Button variant="contained" size="small" className={classes.navButton} onClick={() => navigate('/agents')}>Back to Agents</Button>
          </div>
        </div>
        {awakenError && (
          <Typography style={{ color: '#ff7a7a', fontSize: 12, marginTop: 6 }}>{awakenError}</Typography>
        )}
      </div>

      <Box className="agentDashRoot" style={{ marginTop: 12 }}>
        {loading && (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CircularProgress size={20} />
            <Typography style={{ color: '#bbb' }}>Loading…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Typography style={{ color: '#f44336' }}>{error}</Typography>
        )}

        {!loading && !error && (
          <Box ref={splitRowRef} style={{ display: 'flex', flexDirection: 'row', gap: 0, alignItems: 'stretch', width: '100%' }}>
            <Box style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, paddingRight: 12 }}>
              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left', order: -2 }}>
              {renderSectionHeader('agentStatus', 'Agent Status')}
              {!!openSections.agentStatus && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.agentStatus && <Box style={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', rowGap: 6, columnGap: 10, textAlign: 'left' }}>
                <Typography style={{ color: '#bbb' }}>Score</Typography>
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{`Score = 7d + 0.5 * 24h + 10 * funders\n7d: ${formatNum(scoreInfo.e7, 2)}\n24h: ${formatNum(scoreInfo.e24, 2)}\nfunders: ${formatNum(scoreInfo.funders, 0)}\nScore: ${formatNum(scoreInfo.score, 2)}`}</span>}>
                  <Typography style={{ color: 'white', wordBreak: 'break-word', cursor: 'help' }}>{formatNum(scoreInfo.score, 2) || '0.00'}</Typography>
                </Tooltip>

                <Typography style={{ color: '#bbb' }}>State</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{displayState}</Typography>

                <Typography style={{ color: '#bbb' }}>E3D Balance</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{treasury ? formatNum(treasury.available_e3d_balance != null ? treasury.available_e3d_balance : (agent && agent.e3d_balance), 2) : (agent ? Number(agent.e3d_balance || 0).toFixed(2) : '0.00')}</Typography>

                <Typography style={{ color: '#bbb' }}>Funders</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(scoreInfo.funders, 0) || '0'}</Typography>

                <Typography style={{ color: '#bbb' }}>Total Funded</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(scoreInfo.total, 2) || '0.00'}</Typography>

                <Typography style={{ color: '#bbb' }}>24h / 7d</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{`${formatNum(scoreInfo.e24, 2) || '0.00'} / ${formatNum(scoreInfo.e7, 2) || '0.00'}`}</Typography>

                <Typography style={{ color: '#bbb' }}>Last Funded</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{scoreInfo.lastFundedIso || '—'}</Typography>

                <Typography style={{ color: '#bbb' }}>Last Heartbeat</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{displayHeartbeat || '—'}</Typography>

                <Typography style={{ color: '#bbb' }}>Soul.md</Typography>
                {soulMdHttp ? (
                  <Link href={soulMdHttp} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-word' }}>
                    {soulMdHttp}
                  </Link>
                ) : (
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{agent && agent.soul_md_hash ? agent.soul_md_hash : '(not generated yet)'}</Typography>
                )}

              </Box>}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('treasury', 'Treasury')}
              {!!openSections.treasury && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.treasury && <>
              <Box style={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                <Typography style={{ color: '#bbb' }}>Available Balance</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(treasury && treasury.available_e3d_balance != null ? treasury.available_e3d_balance : (agent && agent.e3d_balance != null ? agent.e3d_balance : 0), 2)}</Typography>

                <Typography style={{ color: '#bbb' }}>Total Funded</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(treasury && treasury.total_funded_e3d != null ? treasury.total_funded_e3d : (fundingStats && fundingStats.total_credited_e3d != null ? fundingStats.total_credited_e3d : 0), 2)}</Typography>

                <Typography style={{ color: '#bbb' }}>Total Burned</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(treasury && treasury.total_burned_e3d != null ? treasury.total_burned_e3d : (burnStats && burnStats.total_burned_e3d != null ? burnStats.total_burned_e3d : 0), 2)}</Typography>

                <Typography style={{ color: '#bbb' }}>Net Remaining</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(treasury && treasury.net_e3d_remaining != null ? treasury.net_e3d_remaining : (agent && agent.e3d_balance != null ? agent.e3d_balance : 0), 2)}</Typography>

                <Typography style={{ color: '#bbb' }}>Funding / Burn Events</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{`${formatNum(treasury && treasury.funding_events != null ? treasury.funding_events : (fundingStats && fundingStats.funding_events != null ? fundingStats.funding_events : 0), 0)} / ${formatNum(treasury && treasury.burn_events != null ? treasury.burn_events : (burnStats && burnStats.burn_events != null ? burnStats.burn_events : 0), 0)}`}</Typography>

                <Typography style={{ color: '#bbb' }}>Last Funded</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(treasury && treasury.last_funded_iso ? treasury.last_funded_iso : '') || '—'}</Typography>

                <Typography style={{ color: '#bbb' }}>Last Burn</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(treasury && treasury.last_burn_iso ? treasury.last_burn_iso : '') || '—'}</Typography>
              </Box>

              {!fundingHistory.length && (
                <Typography style={{ color: '#bbb', marginTop: 12 }}>No funding events recorded.</Typography>
              )}

              {!!fundingHistory.length && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                  {fundingHistory.slice(0, 6).map((entry, idx) => {
                    const txUrl = toEtherscanTxUrl(entry && entry.funding_tx_hash ? entry.funding_tx_hash : '');
                    const key = `${String(entry && entry.ts ? entry.ts : 'funding')}:${idx}`;
                    return (
                      <Box key={key} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                        <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                          <Typography style={{ color: '#bbb' }}>Timestamp</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(entry && entry.ts ? entry.ts : '') || '—'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Credited</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(entry && entry.credited_e3d_amount != null ? entry.credited_e3d_amount : 0, 2)}</Typography>

                          <Typography style={{ color: '#bbb' }}>Funder</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', fontFamily: entry && entry.funder_address ? 'monospace' : 'inherit' }}>{String(entry && entry.funder_address ? entry.funder_address : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Funding Tx</Typography>
                          {txUrl ? (
                            <Link href={txUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>
                              {String(entry && entry.funding_tx_hash ? entry.funding_tx_hash : '—')}
                            </Link>
                          ) : (
                            <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(entry && entry.funding_tx_hash ? entry.funding_tx_hash : '—')}</Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              </>}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('nextBestAction', 'Next Best Action')}
              {!!openSections.nextBestAction && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.nextBestAction && !nextBestAction && (
                <Typography style={{ color: '#bbb' }}>No next action available yet.</Typography>
              )}

              {!!openSections.nextBestAction && !!nextBestAction && (
                <>
                  <Box style={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                    <Typography style={{ color: '#bbb' }}>Title</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(nextBestAction.title || '—')}</Typography>

                    <Typography style={{ color: '#bbb' }}>Why now</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{String(nextBestAction.why_now || '—')}</Typography>

                    <Typography style={{ color: '#bbb' }}>Expected Artifact</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(nextBestAction.expected_artifact || '—')}</Typography>

                    <Typography style={{ color: '#bbb' }}>Estimated Burn</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(nextBestAction.estimated_burn_e3d != null ? nextBestAction.estimated_burn_e3d : 0, 2)} E3D</Typography>

                    <Typography style={{ color: '#bbb' }}>Confidence</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{`${formatNum((Number(nextBestAction.confidence || 0) * 100), 0)}%`}</Typography>

                    <Typography style={{ color: '#bbb' }}>Expected Value</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(nextBestAction.expected_value_score != null ? nextBestAction.expected_value_score : 0, 2)}</Typography>

                    <Typography style={{ color: '#bbb' }}>Strategy</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(nextBestAction.strategy_type || '—')}</Typography>

                    <Typography style={{ color: '#bbb' }}>Action ID</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-all', fontFamily: 'monospace' }}>{String(nextBestAction.action_id || '—')}</Typography>

                    <Typography style={{ color: '#bbb' }}>Updated</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(nextBestAction.updated_at || '') || '—'}</Typography>

                    <Typography style={{ color: '#bbb' }}>Expires</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(nextBestAction.expires_at || '') || '—'}</Typography>
                  </Box>

                  {!!nextBestAction.blocked_reason && (
                    <Typography style={{ color: '#ffb74d', marginTop: 10 }}>
                      Blocked reason: {String(nextBestAction.blocked_reason)}
                    </Typography>
                  )}

                  {!!fundingLadderRows.length && (
                    <Box style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <Typography style={{ color: '#9ea4d8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>Funding Ladder</Typography>
                      {fundingLadderRows.map((row) => (
                        <Box key={row.key} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 8, background: 'rgba(0,0,0,0.2)' }}>
                          <Typography style={{ color: 'white', fontWeight: 700 }}>{row.label}</Typography>
                          <Typography style={{ color: '#9ea4d8' }}>{formatNum(row.amount_e3d, 2)} E3D</Typography>
                          {!!row.note && <Typography style={{ color: '#bbb', marginTop: 4 }}>{row.note}</Typography>}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {!!(nextBestAction.evidence_refs && nextBestAction.evidence_refs.length) && (
                    <Box style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Typography style={{ color: '#9ea4d8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>Evidence</Typography>
                      {nextBestAction.evidence_refs.slice(0, 8).map((ref, idx) => {
                        const val = String(ref || '').trim();
                        if (!val) return null;
                        const isUrl = /^https?:\/\//i.test(val);
                        return isUrl ? (
                          <Link key={`nba_ev_${idx}`} href={val} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>{val}</Link>
                        ) : (
                          <Typography key={`nba_ev_${idx}`} style={{ color: '#bbb', wordBreak: 'break-word' }}>{val}</Typography>
                        );
                      })}
                    </Box>
                  )}
                </>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('artifacts', 'Artifacts')}
              {!!openSections.artifacts && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.artifacts && !artifacts.length && (
                <Typography style={{ color: '#bbb' }}>No artifacts published yet.</Typography>
              )}

              {!!openSections.artifacts && !!artifacts.length && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {artifacts.map((artifact, idx) => {
                    const evidence = Array.isArray(artifact && artifact.evidence_refs) ? artifact.evidence_refs : [];
                    const artifactKey = String((artifact && artifact.artifact_id) || `${artifact && artifact.ts ? artifact.ts : 'artifact'}_${idx}`);
                    return (
                      <Box key={artifactKey} style={{ border: '1px solid #2a2a2a', borderRadius: 10, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                        <Typography style={{ color: 'white', fontWeight: 800, wordBreak: 'break-word' }}>{String((artifact && artifact.title) || (artifact && artifact.artifact_type) || 'Artifact')}</Typography>
                        <Typography style={{ color: '#9ea4d8', fontSize: 12, marginTop: 2 }}>
                          {String(artifact && artifact.artifact_type ? artifact.artifact_type : 'artifact')}
                          {artifact && artifact.ts ? ` • ${String(artifact.ts)}` : ''}
                        </Typography>
                        {!!(artifact && artifact.summary) && (
                          <Typography style={{ color: '#ddd', marginTop: 8, whiteSpace: 'pre-wrap' }}>{String(artifact.summary)}</Typography>
                        )}
                        <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 4, columnGap: 8, marginTop: 8 }}>
                          <Typography style={{ color: '#bbb' }}>Estimated Burn</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(artifact && artifact.estimated_burn_e3d != null ? artifact.estimated_burn_e3d : 0, 2)} E3D</Typography>

                          <Typography style={{ color: '#bbb' }}>Strategy</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(artifact && artifact.strategy_type ? artifact.strategy_type : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Action ID</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-all', fontFamily: 'monospace' }}>{String(artifact && artifact.action_id ? artifact.action_id : '—')}</Typography>
                        </Box>
                        {!!(artifact && artifact.body_ref) && (() => {
                          const href = ipfsToHttpUrl(artifact.body_ref) || String(artifact.body_ref);
                          return (
                            <Box style={{ marginTop: 8 }}>
                              <Link href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>
                                Open body reference
                              </Link>
                            </Box>
                          );
                        })()}
                        {!!evidence.length && (
                          <Box style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {evidence.slice(0, 6).map((ref, jdx) => {
                              const val = String(ref || '').trim();
                              if (!val) return null;
                              const isUrl = /^https?:\/\//i.test(val);
                              return isUrl ? (
                                <Link key={`${artifactKey}_ev_${jdx}`} href={val} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>{val}</Link>
                              ) : (
                                <Typography key={`${artifactKey}_ev_${jdx}`} style={{ color: '#bbb', wordBreak: 'break-word' }}>{val}</Typography>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('valueEvents', 'Value Events')}
              {!!openSections.valueEvents && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.valueEvents && !valueEvents.length && (
                <Typography style={{ color: '#bbb' }}>No value events recorded yet.</Typography>
              )}

              {!!openSections.valueEvents && !!valueEvents.length && (
                <>
                  <Box style={{ display: 'grid', gridTemplateColumns: '180px minmax(0, 1fr)', rowGap: 6, columnGap: 10, marginBottom: 10 }}>
                    <Typography style={{ color: '#bbb' }}>Estimated Value (7d)</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(valueEventsTotalEstimate, 2)}</Typography>

                    <Typography style={{ color: '#bbb' }}>Event Count</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(valueEvents.length)}</Typography>

                    {!!orchestratorSummary && (
                      <>
                        <Typography style={{ color: '#bbb' }}>Summary Events (7d)</Typography>
                        <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(orchestratorSummary.value_event_count_7d != null ? orchestratorSummary.value_event_count_7d : valueEvents.length, 0)}</Typography>
                      </>
                    )}
                  </Box>
                  <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {valueEvents.map((event, idx) => {
                      const evidence = Array.isArray(event && event.evidence_refs) ? event.evidence_refs : [];
                      const eventKey = String((event && event.event_id) || `${event && event.ts ? event.ts : 'event'}_${idx}`);
                      return (
                        <Box key={eventKey} style={{ border: '1px solid #2a2a2a', borderRadius: 10, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                          <Typography style={{ color: 'white', fontWeight: 800, wordBreak: 'break-word' }}>{String((event && event.event_type) || 'value_event')}</Typography>
                          <Typography style={{ color: '#9ea4d8', fontSize: 12, marginTop: 2 }}>{String(event && event.ts ? event.ts : '')}</Typography>
                          <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 4, columnGap: 8, marginTop: 8 }}>
                            <Typography style={{ color: '#bbb' }}>Estimated Value</Typography>
                            <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(event && event.estimated_value != null ? event.estimated_value : 0, 2)}</Typography>

                            <Typography style={{ color: '#bbb' }}>Confidence</Typography>
                            <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{`${formatNum((Number(event && event.confidence != null ? event.confidence : 0) * 100), 0)}%`}</Typography>

                            <Typography style={{ color: '#bbb' }}>Action ID</Typography>
                            <Typography style={{ color: 'white', wordBreak: 'break-all', fontFamily: 'monospace' }}>{String(event && event.related_action_id ? event.related_action_id : '—')}</Typography>

                            <Typography style={{ color: '#bbb' }}>Artifact ID</Typography>
                            <Typography style={{ color: 'white', wordBreak: 'break-all', fontFamily: 'monospace' }}>{String(event && event.related_artifact_id ? event.related_artifact_id : '—')}</Typography>
                          </Box>
                          {!!evidence.length && (
                            <Box style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {evidence.slice(0, 6).map((ref, jdx) => {
                                const val = String(ref || '').trim();
                                if (!val) return null;
                                const isUrl = /^https?:\/\//i.test(val);
                                return isUrl ? (
                                  <Link key={`${eventKey}_ev_${jdx}`} href={val} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>{val}</Link>
                                ) : (
                                  <Typography key={`${eventKey}_ev_${jdx}`} style={{ color: '#bbb', wordBreak: 'break-word' }}>{val}</Typography>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('budgetPolicy', 'Budget Policy')}
              {!!openSections.budgetPolicy && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.budgetPolicy && !budgetPolicy && (
                <Typography style={{ color: '#bbb' }}>No budget policy configured.</Typography>
              )}

              {!!openSections.budgetPolicy && !!budgetPolicy && (
                <Box style={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                  <Typography style={{ color: '#bbb' }}>Treasury State</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(budgetPolicy && budgetPolicy.treasury_state ? budgetPolicy.treasury_state : 'active')}</Typography>

                  <Typography style={{ color: '#bbb' }}>Daily Burn Cap</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(budgetPolicy && budgetPolicy.daily_burn_cap_e3d != null ? budgetPolicy.daily_burn_cap_e3d : 0, 2)}</Typography>

                  <Typography style={{ color: '#bbb' }}>Weekly Burn Cap</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(budgetPolicy && budgetPolicy.weekly_burn_cap_e3d != null ? budgetPolicy.weekly_burn_cap_e3d : 0, 2)}</Typography>

                  <Typography style={{ color: '#bbb' }}>Min Balance</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(budgetPolicy && budgetPolicy.min_balance_for_active_execution != null ? budgetPolicy.min_balance_for_active_execution : 0, 2)}</Typography>

                  <Typography style={{ color: '#bbb' }}>Hibernate Threshold</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(budgetPolicy && budgetPolicy.hibernate_threshold_e3d != null ? budgetPolicy.hibernate_threshold_e3d : 0, 2)}</Typography>

                  <Typography style={{ color: '#bbb' }}>Config</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontFamily: budgetPolicy && budgetPolicy.config && Object.keys(budgetPolicy.config).length ? 'monospace' : 'inherit', fontSize: budgetPolicy && budgetPolicy.config && Object.keys(budgetPolicy.config).length ? 12 : 'inherit' }}>{budgetPolicy && budgetPolicy.config && Object.keys(budgetPolicy.config).length ? safeJsonStringify(budgetPolicy.config, 220) : '—'}</Typography>

                  <Typography style={{ color: '#bbb' }}>Updated</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(budgetPolicy && budgetPolicy.updated_at ? budgetPolicy.updated_at : '') || '—'}</Typography>
                </Box>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('burnLedger', 'Burn Ledger')}
              {!!openSections.burnLedger && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.burnLedger && <>
              <Box style={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr)', rowGap: 6, columnGap: 10, marginBottom: burns.length ? 12 : 0 }}>
                <Typography style={{ color: '#bbb' }}>Burn Events</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(burnStats && burnStats.burn_events != null ? burnStats.burn_events : 0, 0) || '0'}</Typography>

                <Typography style={{ color: '#bbb' }}>Execution-linked Burns</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(burnStats && burnStats.execution_count != null ? burnStats.execution_count : 0, 0) || '0'}</Typography>

                <Typography style={{ color: '#bbb' }}>Total Burned</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(burnStats && burnStats.total_burned_e3d != null ? burnStats.total_burned_e3d : 0, 2)}</Typography>

                <Typography style={{ color: '#bbb' }}>24h / 7d</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{`${formatNum(burnStats && burnStats.burned_e3d_24h != null ? burnStats.burned_e3d_24h : 0, 2)} / ${formatNum(burnStats && burnStats.burned_e3d_7d != null ? burnStats.burned_e3d_7d : 0, 2)}`}</Typography>

                <Typography style={{ color: '#bbb' }}>Last Burn</Typography>
                <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(burnStats && burnStats.last_burn_iso ? burnStats.last_burn_iso : '') || '—'}</Typography>
              </Box>

              {!burns.length && (
                <Typography style={{ color: '#bbb' }}>No burn entries logged.</Typography>
              )}

              {!!burns.length && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {burns.map((burn, idx) => {
                    const key = `${String(burn && burn.ts ? burn.ts : 'burn')}:${idx}`;
                    return (
                      <Box key={key} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                        <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                          <Typography style={{ color: '#bbb' }}>Timestamp</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(burn && burn.ts ? burn.ts : '') || '—'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Amount</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(burn && burn.burn_e3d_amount != null ? burn.burn_e3d_amount : 0, 2)}</Typography>

                          <Typography style={{ color: '#bbb' }}>Reason</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(burn && burn.reason ? burn.reason : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Activity ID</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(burn && burn.activity_id ? burn.activity_id : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Execution ID</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', fontFamily: burn && burn.execution_id ? 'monospace' : 'inherit' }}>{String(burn && burn.execution_id ? burn.execution_id : '—')}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              </>}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('cooldownLedger', 'Cooldown Ledger')}
              {!!openSections.cooldownLedger && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.cooldownLedger && !!orchestratorSummary && (
                <Box style={{ display: 'grid', gridTemplateColumns: '160px minmax(0, 1fr)', rowGap: 6, columnGap: 10, marginBottom: cooldowns.length ? 12 : 0 }}>
                  <Typography style={{ color: '#bbb' }}>Cooldown Blocks (7d)</Typography>
                  <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(orchestratorSummary && orchestratorSummary.cooldown_block_count_7d != null ? orchestratorSummary.cooldown_block_count_7d : cooldowns.length, 0)}</Typography>
                </Box>
              )}

              {!!openSections.cooldownLedger && !cooldowns.length && (
                <Typography style={{ color: '#bbb' }}>No cooldowns recorded.</Typography>
              )}

              {!!openSections.cooldownLedger && !!cooldowns.length && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {cooldowns.map((cooldown, idx) => {
                    const key = `${String(cooldown && cooldown.cooldown_key ? cooldown.cooldown_key : 'cooldown')}:${String(cooldown && cooldown.signature_hash ? cooldown.signature_hash : idx)}`;
                    return (
                      <Box key={key} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                        <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                          <Typography style={{ color: '#bbb' }}>Cooldown Key</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', fontFamily: 'monospace' }}>{String(cooldown && cooldown.cooldown_key ? cooldown.cooldown_key : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Signature</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-all', fontFamily: 'monospace' }}>{String(cooldown && cooldown.signature_hash ? cooldown.signature_hash : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Last Executed</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(cooldown && cooldown.last_executed_at ? cooldown.last_executed_at : '') || '—'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Expires</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(cooldown && cooldown.expires_at ? cooldown.expires_at : '') || '—'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Updated</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(cooldown && cooldown.updated_at ? cooldown.updated_at : '') || '—'}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('resourcePolicies', 'Resource Policies')}
              {!!openSections.resourcePolicies && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.resourcePolicies && !resourcePolicies.length && (
                <Typography style={{ color: '#bbb' }}>No external resource policies configured.</Typography>
              )}

              {!!openSections.resourcePolicies && !!resourcePolicies.length && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {resourcePolicies.map((policy, idx) => {
                    const allowlist = policy && policy.allowlist != null ? policy.allowlist : [];
                    const rateLimit = policy && policy.rate_limit != null ? policy.rate_limit : {};
                    const allowlistSummary = Array.isArray(allowlist)
                      ? (allowlist.length ? allowlist.join(', ') : '—')
                      : safeJsonStringify(allowlist, 220);
                    const rateLimitSummary = rateLimit && Object.keys(rateLimit).length
                      ? safeJsonStringify(rateLimit, 220)
                      : '—';
                    const key = `${String(policy && policy.resource_type ? policy.resource_type : 'resource')}:${String(policy && policy.resource_scope ? policy.resource_scope : idx)}`;
                    return (
                      <Box key={key} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                        <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                          <Typography style={{ color: '#bbb' }}>Type</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(policy && policy.resource_type ? policy.resource_type : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Scope</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(policy && policy.resource_scope ? policy.resource_scope : 'default')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Access</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(policy && policy.access_mode ? policy.access_mode : 'disabled')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Allowlist</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{allowlistSummary || '—'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Rate Limit</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontFamily: typeof rateLimitSummary === 'string' && rateLimitSummary.startsWith('{') ? 'monospace' : 'inherit', fontSize: typeof rateLimitSummary === 'string' && rateLimitSummary.startsWith('{') ? 12 : 'inherit' }}>{rateLimitSummary}</Typography>

                          <Typography style={{ color: '#bbb' }}>Credential Ref</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{policy && policy.credential_ref_present ? 'Configured' : 'None'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Updated</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(policy && policy.updated_at ? policy.updated_at : '') || '—'}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('strategyPolicies', 'Strategy Policies')}
              {!!openSections.strategyPolicies && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.strategyPolicies && !strategies.length && (
                <Typography style={{ color: '#bbb' }}>No strategy policies configured.</Typography>
              )}

              {!!openSections.strategyPolicies && !!strategies.length && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {strategies.map((strategy, idx) => {
                    const config = strategy && strategy.config != null ? strategy.config : {};
                    const configSummary = config && Object.keys(config).length
                      ? safeJsonStringify(config, 220)
                      : '—';
                    const key = `${String(strategy && strategy.strategy_type ? strategy.strategy_type : 'strategy')}:${idx}`;
                    return (
                      <Box key={key} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                        <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                          <Typography style={{ color: '#bbb' }}>Type</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(strategy && strategy.strategy_type ? strategy.strategy_type : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Enabled</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{strategy && strategy.enabled ? 'Yes' : 'No'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Approval</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(strategy && strategy.approval_mode ? strategy.approval_mode : 'auto_internal')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Risk</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(strategy && strategy.risk_level ? strategy.risk_level : 'medium')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Burn / Run</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(strategy && strategy.max_burn_e3d_per_run != null ? strategy.max_burn_e3d_per_run : 0, 2)}</Typography>

                          <Typography style={{ color: '#bbb' }}>Daily Burn Cap</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(strategy && strategy.daily_burn_cap_e3d != null ? strategy.daily_burn_cap_e3d : 0, 2)}</Typography>

                          <Typography style={{ color: '#bbb' }}>Config</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontFamily: typeof configSummary === 'string' && configSummary.startsWith('{') ? 'monospace' : 'inherit', fontSize: typeof configSummary === 'string' && configSummary.startsWith('{') ? 12 : 'inherit' }}>{configSummary}</Typography>

                          <Typography style={{ color: '#bbb' }}>Updated</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(strategy && strategy.updated_at ? strategy.updated_at : '') || '—'}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('executionLedger', 'Execution Ledger')}
              {!!openSections.executionLedger && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.executionLedger && !executions.length && (
                <Typography style={{ color: '#bbb' }}>No execution records logged.</Typography>
              )}

              {!!openSections.executionLedger && !!executions.length && (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {executions.map((execution, idx) => {
                    const result = execution && execution.result != null ? execution.result : {};
                    const evidence = execution && execution.evidence != null ? execution.evidence : {};
                    const resultSummary = result && Object.keys(result).length
                      ? safeJsonStringify(result, 220)
                      : '—';
                    const evidenceSummary = evidence && Object.keys(evidence).length
                      ? safeJsonStringify(evidence, 220)
                      : '—';
                    const key = `${String(execution && execution.execution_id ? execution.execution_id : 'execution')}:${idx}`;
                    return (
                      <Box key={key} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                        <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                          <Typography style={{ color: '#bbb' }}>Execution ID</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', fontFamily: 'monospace' }}>{String(execution && execution.execution_id ? execution.execution_id : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Strategy</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(execution && execution.strategy_type ? execution.strategy_type : '—')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Status</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(execution && execution.status ? execution.status : 'proposed')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Approval</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(execution && execution.approval_mode ? execution.approval_mode : 'proposal_only')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Requested By</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String(execution && execution.requested_by ? execution.requested_by : 'system')}</Typography>

                          <Typography style={{ color: '#bbb' }}>Estimated Burn</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(execution && execution.estimated_burn_e3d != null ? execution.estimated_burn_e3d : 0, 2)}</Typography>

                          <Typography style={{ color: '#bbb' }}>Actual Burn</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(execution && execution.actual_burn_e3d != null ? execution.actual_burn_e3d : 0, 2)}</Typography>

                          <Typography style={{ color: '#bbb' }}>Input Hash</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{execution && execution.input_hash_present ? 'Provided' : 'None'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Started</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(execution && execution.started_at ? execution.started_at : '') || '—'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Finished</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(execution && execution.finished_at ? execution.finished_at : '') || '—'}</Typography>

                          <Typography style={{ color: '#bbb' }}>Result</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontFamily: typeof resultSummary === 'string' && resultSummary.startsWith('{') ? 'monospace' : 'inherit', fontSize: typeof resultSummary === 'string' && resultSummary.startsWith('{') ? 12 : 'inherit' }}>{resultSummary}</Typography>

                          <Typography style={{ color: '#bbb' }}>Evidence</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontFamily: typeof evidenceSummary === 'string' && evidenceSummary.startsWith('{') ? 'monospace' : 'inherit', fontSize: typeof evidenceSummary === 'string' && evidenceSummary.startsWith('{') ? 12 : 'inherit' }}>{evidenceSummary}</Typography>

                          <Typography style={{ color: '#bbb' }}>Updated</Typography>
                          <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{normalizeDisplayIso(execution && execution.updated_at ? execution.updated_at : '') || '—'}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader(
                'recommendedActions',
                'Recommended Actions',
                <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Box style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.10)', color: '#e5e7ff' }}>Dry-run</Box>
                  <Box style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: '#9ea4d8' }}>Not executed</Box>
                </Box>
              )}
              {!!openSections.recommendedActions && <Divider style={{ background: '#333', marginTop: 10, marginBottom: 12 }} />}

              {!!openSections.recommendedActions && !recommendationsInfo.latest && (
                <Typography style={{ color: '#bbb' }}>No recommendations yet.</Typography>
              )}

              {!!openSections.recommendedActions && !!recommendationsInfo.latest && (
                <>
                  <Box style={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', rowGap: 6, columnGap: 10 }}>
                    <Typography style={{ color: '#bbb' }}>Generated</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{recommendationsInfo.latest.generated_at || recommendationsInfo.latest.ts || '—'}</Typography>

                    <Typography style={{ color: '#bbb' }}>Run ID</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{recommendationsInfo.latest.run_id || '—'}</Typography>

                    <Typography style={{ color: '#bbb' }}>Summary</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{recommendationsInfo.latest.summary || '—'}</Typography>

                    <Typography style={{ color: '#bbb' }}>Count</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{String((recommendationsInfo.recommendations || []).length)}</Typography>
                  </Box>

                  {recommendationsInfo.unknownCount > 0 && (
                    <Typography style={{ color: '#ffb74d', fontSize: 12, marginTop: 10 }}>
                      {recommendationsInfo.unknownCount} recommendation(s) have an unknown action type.
                    </Typography>
                  )}

                  <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                    {(recommendationsInfo.recommendations || []).map((r, idx) => {
                      const conf = (r && r.confidence != null && Number.isFinite(Number(r.confidence)))
                        ? Math.max(0, Math.min(1, Number(r.confidence)))
                        : null;
                      const confLabel = conf == null ? '—' : `${Math.round(conf * 100)}%`;
                      const risk = String((r && r.risk_level) || '').toLowerCase();
                      const riskLabel = risk ? risk.toUpperCase() : '—';
                      const actionType = String((r && r.action_type) || '');
                      const key = String((r && r.id) || actionType || `rec_${idx}`);
                      return (
                        <Box key={key} style={{ border: '1px solid #2a2a2a', borderRadius: 10, padding: 10, background: 'rgba(5, 8, 20, 0.35)' }}>
                          <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                            <Box style={{ minWidth: 0 }}>
                              <Typography style={{ color: 'white', fontWeight: 800, wordBreak: 'break-word' }}>{(r && r.title) || 'Recommendation'}</Typography>
                              <Typography style={{ color: (r && r.action_type && r.action_type_allowed) ? '#9ea4d8' : '#ffb74d', fontSize: 12, marginTop: 2, wordBreak: 'break-word' }}>{actionType}</Typography>
                            </Box>
                            <Box style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Box style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.08)', color: '#e5e7ff' }}>Confidence: {confLabel}</Box>
                              <Box style={{ fontSize: 10, fontWeight: 900, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: riskColor(risk) }}>Risk: {riskLabel}</Box>
                            </Box>
                          </Box>

                          {!!(r && r.expires_at) && (
                            <Typography style={{ color: '#bbb', fontSize: 12, marginTop: 6 }}>Expires: {String(r.expires_at)}</Typography>
                          )}

                          {!!(r && r.rationale) && (
                            <Typography style={{ color: '#ddd', marginTop: 8, whiteSpace: 'pre-wrap' }}>{String(r.rationale)}</Typography>
                          )}

                          {!!(r && r.dependencies && r.dependencies.length) && (
                            <Typography style={{ color: '#bbb', fontSize: 12, marginTop: 8 }}>Depends on: {r.dependencies.join(', ')}</Typography>
                          )}

                          {r && r.params != null && (
                            <Box style={{ marginTop: 8, padding: 8, borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.25)' }}>
                              <Typography style={{ color: '#9ea4d8', fontSize: 11, fontWeight: 900, letterSpacing: 0.4, textTransform: 'uppercase' }}>Params</Typography>
                              <Typography style={{ color: 'white', fontFamily: 'monospace', fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap' }}>{safeJsonStringify(r.params)}</Typography>
                            </Box>
                          )}

                          {!!(r && r.evidence_links && r.evidence_links.length) && (
                            <Box style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {r.evidence_links.slice(0, 6).map((u) => (
                                <Link key={u} href={u} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>{u}</Link>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {!!(recommendationsInfo.parseErrors && recommendationsInfo.parseErrors.length) && (
                    <Typography style={{ color: '#bbb', fontSize: 12, marginTop: 12 }}>
                      {recommendationsInfo.parseErrors.length} recommendation log(s) could not be parsed.
                    </Typography>
                  )}
                </>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left' }}>
              {renderSectionHeader('soulMd', 'Soul.md')}
              {!!openSections.soulMd && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.soulMd && soulMdHttp && (
                <Typography style={{ color: '#bbb', marginBottom: 8 }}>
                  <Link href={soulMdHttp} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-word' }}>
                    Open soul.md
                  </Link>
                </Typography>
              )}

              {!!openSections.soulMd && soulMdLoading && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <CircularProgress size={18} />
                  <Typography style={{ color: '#bbb' }}>Loading soul.md…</Typography>
                </Box>
              )}

              {!!openSections.soulMd && !soulMdLoading && soulMdError && (
                <Typography style={{ color: '#f44336' }}>{soulMdError}</Typography>
              )}

              {!!openSections.soulMd && !soulMdLoading && !soulMdError && soulMdText && (
                <Typography style={{ color: 'white', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.3 }}>
                  {soulMdText}
                </Typography>
              )}

              {!!openSections.soulMd && !soulMdLoading && !soulMdError && !soulMdText && (
                <Typography style={{ color: '#bbb' }}>(empty)</Typography>
              )}
              </Paper>

              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 12, textAlign: 'left', order: -1 }}>
              {renderSectionHeader('fundAgent', 'Fund Agent')}
              {!!openSections.fundAgent && <Divider style={{ background: '#333', marginTop: 8, marginBottom: 12 }} />}

              {!!openSections.fundAgent && !fundingDisabled && fundingInfoLoading && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <CircularProgress size={18} />
                  <Typography style={{ color: '#bbb' }}>Loading funding info…</Typography>
                </Box>
              )}

              {!!openSections.fundAgent && !fundingDisabled && !fundingInfoLoading && fundingInfoError && (
                <Typography style={{ color: '#f44336' }}>{fundingInfoError}</Typography>
              )}

              {!!openSections.fundAgent && !fundingDisabled && !fundingInfoLoading && !fundingInfoError && (
                <>
                  <Typography style={{ color: '#bbb', marginBottom: 6, textAlign: 'left' }}>
                    Fund this agent directly from MetaMask with E3D, or buy E3D with ETH and send it to escrow.
                  </Typography>

                  {!!nextBestAction && (
                    <Box style={{ border: '1px solid #2a2a2a', borderRadius: 10, padding: 10, marginTop: 10, textAlign: 'left', background: 'rgba(5, 8, 20, 0.35)' }}>
                      <Typography style={{ color: 'white', fontWeight: 700 }}>
                        Next action: {String(nextBestAction.title || nextBestAction.action_id || 'Proposal pending')}
                      </Typography>
                      {!!nextBestAction.why_now && (
                        <Typography style={{ color: '#bbb', marginTop: 6, whiteSpace: 'pre-wrap' }}>
                          {String(nextBestAction.why_now)}
                        </Typography>
                      )}
                      {!!fundingLadderRows.length && (
                        <Typography style={{ color: '#9ea4d8', marginTop: 6 }}>
                          Ladder starts at {formatNum(fundingLadderRows[0].amount_e3d, 2)} E3D ({fundingLadderRows[0].label}).
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Box style={{ border: '1px solid #2a2a2a', borderRadius: 10, padding: 12, marginTop: 10, textAlign: 'left' }}>
                    <Typography style={{ color: 'white', fontWeight: 600, marginBottom: 6, textAlign: 'left' }}>Quick Fund</Typography>
                    <Typography style={{ color: '#bbb', marginBottom: 10, textAlign: 'left' }}>
                      Choose E3D or ETH, enter a funding amount, and see an estimate of how long it can keep this agent active.
                    </Typography>

                    <RadioGroup
                      row
                      value={quickFundMode}
                      onChange={(e) => setQuickFundMode(String(e.target.value || 'e3d'))}
                      style={{ marginBottom: 10 }}
                    >
                      <FormControlLabel
                        value="e3d"
                        control={<Radio style={{ color: '#4fc3f7' }} />}
                        label={<Typography style={{ color: 'white' }}>E3D</Typography>}
                      />
                      <FormControlLabel
                        value="eth"
                        control={<Radio style={{ color: '#4fc3f7' }} />}
                        label={<Typography style={{ color: 'white' }}>ETH</Typography>}
                      />
                    </RadioGroup>

                    <Box style={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', rowGap: 6, columnGap: 10, textAlign: 'left' }}>
                      <Typography style={{ color: '#bbb' }}>Wallet</Typography>
                      {fundingWalletAddressUrl ? (
                        <Link href={fundingWalletAddressUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>
                          {fundingWalletAddress}
                        </Link>
                      ) : (
                        <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{fundingWalletAddress || 'Not connected'}</Typography>
                      )}

                      <Typography style={{ color: '#bbb' }}>{fundingWalletBalanceLabel}</Typography>
                      <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{fundingWalletBalanceDisplay || '0.0000'}</Typography>
                    </Box>

                    <Box style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
                      <TextField
                        value={quickFundAmount}
                        onChange={(e) => setQuickFundAmount(e.target.value)}
                        label={quickFundMode === 'eth' ? 'Funding Target (E3D estimate)' : 'Funding Amount (E3D)'}
                        variant="outlined"
                        fullWidth
                        className={classes.input}
                        InputLabelProps={{ style: { color: '#bbb' } }}
                        InputProps={{ style: { color: 'white' } }}
                      />

                      {!!fundingEstimateSummary && (
                        <Typography style={{ color: '#9ea4d8' }}>{fundingEstimateSummary}</Typography>
                      )}

                      {!!fundingLadderHint && (
                        <Typography style={{ color: '#9ea4d8' }}>{fundingLadderHint}</Typography>
                      )}

                      {quickFundMode === 'eth' && (
                        <Typography style={{ color: '#bbb' }}>
                          You&apos;ll continue to the swap page to buy E3D with ETH and send it to this agent&apos;s escrow.
                        </Typography>
                      )}

                      <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        {quickFundMode === 'e3d' ? (
                          <Button
                            className={classes.fundingCtaButton}
                            variant="contained"
                            color="primary"
                            disabled={quickFundBusy || finalizeBusy || !String(quickFundAmount || '').trim()}
                            onClick={handleQuickFund}
                          >
                            {quickFundBusy ? 'Sending…' : 'Fund with E3D'}
                          </Button>
                        ) : (
                          <Button
                            className={classes.fundingCtaButton}
                            component={RouterLink}
                            to={`/swap?recipient=${encodeURIComponent(escrowAddr || '')}&returnTo=${encodeURIComponent(location.pathname)}&fundAgent=1&targetE3d=${encodeURIComponent(quickFundAmount || '')}`}
                            variant="contained"
                            color="primary"
                            disabled={!escrowAddr || !String(quickFundAmount || '').trim()}
                          >
                            Continue with ETH
                          </Button>
                        )}
                      </Box>

                      {quickFundError && (
                        <Typography style={{ color: '#f44336' }}>{quickFundError}</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box style={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', rowGap: 6, columnGap: 10, marginTop: 8, textAlign: 'left' }}>
                    <Typography style={{ color: '#bbb' }}>Available Balance</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(treasury && treasury.available_e3d_balance != null ? treasury.available_e3d_balance : (agent && agent.e3d_balance != null ? agent.e3d_balance : 0), 2)}</Typography>

                    <Typography style={{ color: '#bbb' }}>Total Burned</Typography>
                    <Typography style={{ color: 'white', wordBreak: 'break-word' }}>{formatNum(treasury && treasury.total_burned_e3d != null ? treasury.total_burned_e3d : (burnStats && burnStats.total_burned_e3d != null ? burnStats.total_burned_e3d : 0), 2)}</Typography>

                    <Typography style={{ color: '#bbb' }}>Escrow</Typography>
                    {escrowAddressUrl ? (
                      <Link href={escrowAddressUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>
                        {escrowAddr}
                      </Link>
                    ) : (
                      <Typography style={{ color: 'white', wordBreak: 'break-all' }}>{escrowAddr || '(missing)'}</Typography>
                    )}

                    <Typography style={{ color: '#bbb' }}>Receipt Manager</Typography>
                    {receiptMgrUrl ? (
                      <Link href={receiptMgrUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7', wordBreak: 'break-all' }}>
                        {receiptMgrAddr}
                      </Link>
                    ) : (
                      <Typography style={{ color: 'white', wordBreak: 'break-all' }}>{receiptMgrAddr || '(missing)'}</Typography>
                    )}

                    <Typography style={{ color: '#bbb' }}>Buy with ETH</Typography>
                    <Link component={RouterLink} to={`/swap?recipient=${encodeURIComponent(escrowAddr || '')}&returnTo=${encodeURIComponent(location.pathname)}&fundAgent=1`} style={{ color: '#4fc3f7' }}>
                      Open ETH→E3D Funding
                    </Link>
                  </Box>

                  <Box style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
                    <Typography style={{ color: '#bbb', textAlign: 'left' }}>
                      Manual fallback
                    </Typography>
                    <TextField
                      value={fundingTxHash}
                      onChange={(e) => setFundingTxHash(e.target.value)}
                      label="Funding Tx Hash"
                      variant="outlined"
                      fullWidth
                      className={classes.input}
                      InputLabelProps={{ style: { color: '#bbb' } }}
                      InputProps={{ style: { color: 'white' } }}
                    />

                    {fundingTxUrl && (
                      <Typography style={{ color: '#bbb' }}>
                        <Link href={fundingTxUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4fc3f7' }}>
                          View on Etherscan
                        </Link>
                      </Typography>
                    )}

                    <TextField
                      value={funderAddress}
                      onChange={(e) => setFunderAddress(e.target.value)}
                      label="Funder Address (optional)"
                      variant="outlined"
                      fullWidth
                      className={classes.input}
                      InputLabelProps={{ style: { color: '#bbb' } }}
                      InputProps={{ style: { color: 'white' } }}
                    />

                    <Box style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={finalizeBusy || !String(fundingTxHash || '').trim()}
                        onClick={handleFinalizeFunding}
                      >
                        {finalizeBusy ? 'Finalizing…' : 'Finalize Funding'}
                      </Button>

                      <Button
                        variant="outlined"
                        style={{ borderColor: '#4fc3f7', color: '#4fc3f7' }}
                        disabled={mintBusy || !finalizeResult || !finalizeResult.receipt_metadata_uri}
                        onClick={handleMintReceipt}
                      >
                        {mintBusy ? 'Minting…' : 'Mint Receipt NFT'}
                      </Button>
                    </Box>

                    {finalizeError && (
                      <Typography style={{ color: '#f44336' }}>{finalizeError}</Typography>
                    )}

                    {finalizeResult && finalizeResult.ok && (
                      <Box style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10 }}>
                        <Typography style={{ color: 'white', fontWeight: 600, textAlign: 'left' }}>Finalized</Typography>
                        <Typography style={{ color: '#bbb', marginTop: 4 }}>
                          Credited: {Number(finalizeResult.credited_e3d_amount || 0).toFixed(4)} E3D
                        </Typography>
                        <Typography style={{ color: '#bbb' }}>
                          Receipt metadata:{' '}
                          <Link
                            href={ipfsToHttpUrl(finalizeResult.receipt_metadata_uri || '') || String(finalizeResult.receipt_metadata_uri || '')}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#4fc3f7', wordBreak: 'break-word' }}
                          >
                            {String(finalizeResult.receipt_metadata_uri || '')}
                          </Link>
                        </Typography>
                      </Box>
                    )}

                    {finalizeResult && finalizeResult.pending && (
                      <Box style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10 }}>
                        <Typography style={{ color: 'white', fontWeight: 600, textAlign: 'left' }}>Pending confirmations</Typography>
                        <Typography style={{ color: '#bbb', marginTop: 4 }}>
                          Confirmations: {Number(finalizeResult.confirmations || 0)} / {Number(finalizeResult.required_confirmations || 0)}
                        </Typography>
                      </Box>
                    )}

                    {!!jobId && (
                      <Box style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10 }}>
                        <Typography style={{ color: 'white', fontWeight: 600, textAlign: 'left' }}>Agent Creation Job</Typography>
                        <Typography style={{ color: '#bbb', marginTop: 4, wordBreak: 'break-all' }}>Job ID: {jobId}</Typography>
                        {jobBusy && (
                          <Typography style={{ color: '#bbb', marginTop: 6 }}>Working…</Typography>
                        )}
                        {jobData && (
                          <Typography style={{ color: '#bbb', marginTop: 6 }}>
                            Status: {jobData.status || ''}{jobData.stage ? ` (${jobData.stage})` : ''}
                          </Typography>
                        )}
                        {jobData && jobData.status === 'failed' && (
                          <Typography style={{ color: '#f44336', marginTop: 6 }}>{jobData.error || 'Job failed'}</Typography>
                        )}
                        {jobData && jobData.status === 'ready' && (
                          <Typography style={{ color: '#4fc3f7', marginTop: 6 }}>Ready</Typography>
                        )}
                      </Box>
                    )}

                    {jobError && (
                      <Typography style={{ color: '#f44336' }}>{jobError}</Typography>
                    )}

                    {mintError && (
                      <Typography style={{ color: '#f44336' }}>{mintError}</Typography>
                    )}

                    {receiptTokenId && (
                      <Typography style={{ color: '#4fc3f7' }}>Receipt NFT minted. Token ID: {receiptTokenId}</Typography>
                    )}
                  </Box>
                </>
              )}
              </Paper>
            </Box>

            <Box
              role="separator"
              aria-orientation="vertical"
              onMouseDown={(e) => {
                try {
                  resizingRef.current = true;
                  resizeStartXRef.current = e.clientX;
                  resizeStartRatioRef.current = recentWidthRatio;
                  if (document && document.body) document.body.style.cursor = 'col-resize';
                  if (document && document.body) document.body.style.userSelect = 'none';
                } catch (err) {
                }
              }}
              onDoubleClick={() => {
                setRecentWidthRatio(0.33);
              }}
              style={{
                width: 8,
                cursor: 'col-resize',
                background: 'rgba(255,255,255,0.08)',
                borderRadius: 6,
                marginRight: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'sticky',
                top: stickyTopPx,
                height: recentHeightPx,
              }}
            >
              <Box style={{ width: 2, height: 28, background: 'rgba(255,255,255,0.45)', borderRadius: 2 }} />
            </Box>

            <Box style={{ width: recentWidthPx, minWidth: 260, maxWidth: 900, display: 'flex', flexDirection: 'column', position: 'sticky', top: stickyTopPx, height: recentHeightPx }}>
              <Paper style={{ background: '#1a1a1a', border: '1px solid #333', padding: 14, textAlign: 'left', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" style={{ color: 'white', marginBottom: 8, textAlign: 'left' }}>Recent Activity</Typography>
                <Divider style={{ background: '#333', marginBottom: 12 }} />

                <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  {!activities.length && (
                    <Typography style={{ color: '#bbb' }}>No activities yet.</Typography>
                  )}

                  {!!activities.length && (
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left' }}>
                      {activities.map((a, idx) => {
                        const evidence = safeJsonParse(a.evidence_json);
                        return (
                          <Box key={`${a.ts}-${idx}`} style={{ border: '1px solid #2a2a2a', borderRadius: 8, padding: 10 }}>
                            <Typography style={{ color: 'white', fontWeight: 600 }}>
                              {a.type || 'activity'}
                            </Typography>
                            <Typography style={{ color: '#bbb', fontSize: 12 }}>{a.ts || ''}</Typography>
                            <Typography style={{ color: '#ddd', marginTop: 6 }}>{a.summary || ''}</Typography>
                            {evidence && evidence.url && (
                              <Box style={{ marginTop: 6 }}>
                                <Link href={String(evidence.url)} target="_blank" rel="noreferrer" style={{ color: '#4fc3f7' }}>
                                  Evidence
                                </Link>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
}
